/**
 * AuthContext
 *
 * Unified authentication state management.
 * Uses Firebase Auth as the single source of truth.
 * Supports lazy migration of legacy Firestore-only users to Firebase Auth.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  onAuthStateChanged as firebaseOnAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile as firebaseUpdateProfile,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../services/firebase';
import {
  doc, getDoc, setDoc, updateDoc, runTransaction,
  serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { registerWebPush } from '../services/webPush';

export interface User {
  uid: string;
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  photoURL?: string;
  phone?: string;
  role: 'guest' | 'host' | 'admin';
  emailVerified: boolean;
  createdAt: string;
  wpUserId?: string;
  needsPasswordMigration?: boolean;
  listingCount?: number;
  profileComplete?: boolean;
  hostProfile?: {
    responseRate: number;
    responseTime: string;
    isSuperhost: boolean;
  };
  /** Admin flag, independent of `role`. `role` doubles as the guest/host view
   *  mode, so admin access must not hang off it — switching to host would
   *  otherwise revoke it permanently. Server-set only; clients cannot write it. */
  isAdmin?: boolean;
  /** True when the Firestore profile could not be read, so `role` below is a
   *  fallback and not a value we actually loaded. Lets the UI distinguish
   *  "couldn't load your profile" from "you aren't authorised". */
  profileLoadFailed?: boolean;
}

export interface RegisterData {
  email: string;
  password: string;
  displayName: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  needsPasswordMigration: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  setMigratedPassword: (newPassword: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  sendActivationEmail: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Build a User object from a Firestore document
 */
function buildUserFromFirestore(docId: string, data: Record<string, unknown>): User {
  let createdAtStr = new Date().toISOString();
  const createdAt = data.createdAt as { _seconds?: number; toDate?: () => Date } | undefined;
  if (createdAt) {
    if (createdAt._seconds) {
      createdAtStr = new Date(createdAt._seconds * 1000).toISOString();
    } else if (createdAt instanceof Timestamp) {
      createdAtStr = createdAt.toDate().toISOString();
    }
  }

  return {
    uid: docId,
    email: (data.email as string) || '',
    displayName: (data.displayName as string) || (data.email as string)?.split('@')[0] || '',
    firstName: data.firstName as string | undefined,
    lastName: data.lastName as string | undefined,
    photoURL: data.photoURL as string | undefined,
    phone: data.phone as string | undefined,
    role: (data.role as 'guest' | 'host' | 'admin') || 'guest',
    emailVerified: (data.emailVerified as boolean) || false,
    createdAt: createdAtStr,
    wpUserId: data.wpUserId as string | undefined,
    needsPasswordMigration: false,
    listingCount: (data.listingCount as number) || 0,
    profileComplete: (data.profileComplete as boolean) || false,
    hostProfile: data.hostProfile as User['hostProfile'],
    isAdmin: data.isAdmin === true,
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsPasswordMigration, setNeedsPasswordMigration] = useState(false);

  // Pending migration data (stored between login and setMigratedPassword)
  const [pendingMigration, setPendingMigration] = useState<{
    firestoreDocId: string;
    email: string;
    userData: Record<string, unknown>;
  } | null>(null);

  // Listen to Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = firebaseOnAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Register this browser for web push (fire-and-forget, never blocks auth).
        registerWebPush(firebaseUser.uid);
        // Fetch full profile from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            const syncUpdates: Record<string, unknown> = {};

            // Sync Firestore email after a verified email change in Firebase Auth
            // (verifyBeforeUpdateEmail completes outside the app).
            const authEmail = firebaseUser.email?.toLowerCase();
            if (authEmail && (data.email as string)?.toLowerCase() !== authEmail) {
              syncUpdates.email = authEmail;
              syncUpdates.emailVerified = firebaseUser.emailVerified;
              data.email = authEmail;
            }

            // First authenticated session of a SQL-migrated user = account
            // retrieved. Lets admins tell claimed legacy accounts from
            // abandoned ones (covers the activation reset-link path, which
            // completes on Firebase's hosted page outside the app).
            if (data.wpUserId && !data.accountRetrieved) {
              syncUpdates.accountRetrieved = true;
              syncUpdates.accountRetrievedAt = serverTimestamp();
              syncUpdates.retrievalMethod = data.retrievalMethod || 'login';
            }

            if (Object.keys(syncUpdates).length > 0) {
              syncUpdates.updatedAt = serverTimestamp();
              updateDoc(userDoc.ref, syncUpdates).catch((err) =>
                console.error('Failed to sync user doc:', err)
              );
            }

            const profile = buildUserFromFirestore(firebaseUser.uid, data);
            setUser(profile);
          } else {
            // Firebase Auth account with no Firestore doc. Create it now:
            // every later write (Become a Host, profile edits, messaging)
            // goes through `updateDoc`, and an update against a missing doc
            // is rejected by the rules as "You don't have permission to do
            // that" — the user would be permanently stuck.
            const displayName =
              firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User';
            const newProfile = {
              email: firebaseUser.email?.toLowerCase() || '',
              displayName,
              photoURL: firebaseUser.photoURL || '',
              role: 'guest' as const,
              emailVerified: firebaseUser.emailVerified,
              isSuspended: false,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            };
            try {
              // Re-read inside a transaction before writing. merge:true is NOT
              // enough on its own: merge still overwrites every field present
              // in the payload, and this payload carries role:'guest' — so a
              // read that wrongly reported "missing" (a race, a transient
              // failure) would demote a real profile, including the admin
              // account, locking it out of /admin permanently. Only write when
              // the doc genuinely does not exist.
              await runTransaction(db, async (tx) => {
                const ref = doc(db, 'users', firebaseUser.uid);
                const fresh = await tx.get(ref);
                if (fresh.exists()) return; // someone else created it; leave it alone
                tx.set(ref, newProfile);
              });
            } catch (err) {
              console.error('Failed to create user doc:', err);
            }

            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName,
              photoURL: firebaseUser.photoURL || undefined,
              role: 'guest',
              emailVerified: firebaseUser.emailVerified,
              createdAt: new Date().toISOString(),
            });
          }
        } catch (err) {
          // The profile read failed. Do NOT quietly present the user as a
          // guest: for a host or the admin that is a silent privilege
          // downgrade that looks identical to "you aren't authorised", which
          // is exactly how the admin lockout presented. Retry once, then flag
          // the failure so the UI can say "couldn't load your profile"
          // instead of asserting a role we never actually read.
          console.error('Failed to fetch user profile:', err);
          try {
            const retry = await getDoc(doc(db, 'users', firebaseUser.uid));
            if (retry.exists()) {
              setUser(buildUserFromFirestore(firebaseUser.uid, retry.data()));
              setIsLoading(false);
              return;
            }
          } catch (retryErr) {
            console.error('Profile retry also failed:', retryErr);
          }
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || '',
            role: 'guest',
            emailVerified: firebaseUser.emailVerified,
            createdAt: new Date().toISOString(),
            profileLoadFailed: true,
          });
        }
      } else {
        setUser(null);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /**
   * Login - handles Firebase Auth users and legacy (WP-imported) Firestore-only users.
   *
   * Strategy:
   * 1. Try `signInWithEmailAndPassword` first. If it succeeds, we're done.
   * 2. If it fails, ask the `legacyLogin` callable what to do. It runs with the
   *    Admin SDK because Firestore rules forbid signed-out reads of /users —
   *    doing this lookup in the browser always failed with permission-denied,
   *    which surfaced as a generic "we couldn't sign you in" for every legacy
   *    account. It also keeps the legacy password hash on the server.
   *
   * We deliberately do NOT use `fetchSignInMethodsForEmail` here - projects with
   * email-enumeration protection enabled return empty methods for every email,
   * which would falsely route already-migrated users back into the migration modal.
   */
  const login = useCallback(async (email: string, password: string, _remember?: boolean) => {
    setIsLoading(true);

    try {
      const normalizedEmail = email.trim();

      // Step 1: Try Firebase Auth directly.
      try {
        await signInWithEmailAndPassword(auth, normalizedEmail, password);
        setIsLoading(false);
        return;
      } catch (signInErr: unknown) {
        const sErr = signInErr as { code?: string };
        // Re-throw anything that's not a credential-mismatch / user-missing error.
        if (
          sErr.code !== 'auth/invalid-credential' &&
          sErr.code !== 'auth/user-not-found' &&
          sErr.code !== 'auth/wrong-password'
        ) {
          setIsLoading(false);
          throw signInErr;
        }
      }

      // Step 2: Sign-in failed. Let the server decide (legacy account? wrong
      // password? migration pending?).
      const fn = httpsCallable<
        { email: string; password: string },
        { status: 'migrated' | 'needs-activation' | 'invalid' }
      >(functions, 'legacyLogin');

      let status: 'migrated' | 'needs-activation' | 'invalid';
      try {
        const res = await fn({ email: normalizedEmail.toLowerCase(), password });
        status = res.data.status;
      } catch (fnErr: unknown) {
        const e = fnErr as { code?: string; message?: string };
        if (e.code === 'functions/not-found') {
          setIsLoading(false);
          throw { code: 'auth/user-not-found', message: 'No account found with this email address.' };
        }
        if (e.code === 'functions/resource-exhausted') {
          setIsLoading(false);
          throw { code: 'auth/too-many-requests', message: 'Too many attempts. Please try again in a few minutes.' };
        }
        throw fnErr;
      }

      if (status === 'migrated') {
        // The server set this password on the Firebase Auth account.
        await signInWithEmailAndPassword(auth, normalizedEmail, password);
        setIsLoading(false);
        return;
      }

      if (status === 'needs-activation') {
        // The UI shows an "Activate account" card; no email is sent without
        // the user explicitly asking for it (see sendActivationEmail).
        setIsLoading(false);
        throw {
          code: 'auth/needs-activation',
          message: 'This account needs to be activated.',
        };
      }

      setIsLoading(false);
      throw { code: 'auth/wrong-password', message: 'Invalid email or password.' };
    } catch (error: unknown) {
      setIsLoading(false);
      if (error && typeof error === 'object' && 'code' in error) {
        throw error;
      }
      console.error('Login error:', error);
      throw { code: 'auth/internal-error', message: "We couldn't sign you in right now. Please try again in a moment." };
    }
  }, []);

  /**
   * Set password for migrated users
   * Creates a Firebase Auth account and updates Firestore
   */
  const setMigratedPassword = useCallback(async (newPassword: string) => {
    if (!pendingMigration) throw new Error('No pending migration');

    if (newPassword.length < 8) {
      throw { code: 'auth/weak-password', message: 'Password must be at least 8 characters.' };
    }

    const { firestoreDocId, email, userData } = pendingMigration;

    // Try to create a fresh Firebase Auth account. If one already exists
    // for this email (Firestore/Auth state drift), attempt to sign in with
    // the password the user just typed - that recovers the account silently.
    // We avoid `fetchSignInMethodsForEmail` because email-enumeration
    // protection makes it return empty for valid accounts.
    let credential;
    try {
      credential = await createUserWithEmailAndPassword(auth, email, newPassword);
    } catch (createErr: unknown) {
      const cErr = createErr as { code?: string };
      if (cErr.code === 'auth/email-already-in-use') {
        try {
          credential = await signInWithEmailAndPassword(auth, email, newPassword);
        } catch {
          throw {
            code: 'auth/email-already-in-use',
            message:
              'An account already exists for this email. Please go back and log in with your existing password, or use "Forgot password?" to reset it.',
          };
        }
      } else {
        throw createErr;
      }
    }

    if (userData.displayName) {
      await firebaseUpdateProfile(credential.user, { displayName: userData.displayName as string });
    }

    // Copy/update Firestore doc under Firebase Auth UID
    if (firestoreDocId !== credential.user.uid) {
      await setDoc(doc(db, 'users', credential.user.uid), {
        ...userData,
        passwordHash: null,
        passwordSet: true,
        needsPasswordMigration: false,
        firebaseAuthMigrated: true,
        migratedFrom: firestoreDocId,
        accountRetrieved: true,
        accountRetrievedAt: serverTimestamp(),
        retrievalMethod: 'set-password',
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(doc(db, 'users', firestoreDocId), {
        passwordHash: null,
        passwordSet: true,
        needsPasswordMigration: false,
        firebaseAuthMigrated: true,
        accountRetrieved: true,
        accountRetrievedAt: serverTimestamp(),
        retrievalMethod: 'set-password',
        updatedAt: serverTimestamp(),
      });
    }

    setNeedsPasswordMigration(false);
    setPendingMigration(null);
    // onAuthStateChanged will update user state
  }, [pendingMigration]);

  const register = useCallback(async (_data: RegisterData) => {
    // Registration is handled by the OnboardingPage directly via services/auth.ts
    // This is kept for interface compatibility
    throw {
      code: 'auth/use-onboarding',
      message: 'Please use the sign-up page to register.',
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setNeedsPasswordMigration(false);
      setPendingMigration(null);
      // Clean up old localStorage keys
      localStorage.removeItem('darak_user');
      sessionStorage.removeItem('darak_user');
      toast.success('Signed out');
    } catch (err) {
      console.error('Sign-out error:', err);
      toast.error("We couldn't sign you out. Please try again.");
    }
  }, []);

  /**
   * Send an account-activation email (for migration-pending accounts).
   * Routes through the sendAuthEmail Cloud Function so the email comes from
   * Darak's branded template (not Firebase's default template).
   */
  const sendActivationEmail = useCallback(async (email: string) => {
    const fn = httpsCallable<{ email: string; type: 'activation' }, { success: boolean }>(
      functions,
      'sendAuthEmail'
    );
    try {
      await fn({ email: email.trim().toLowerCase(), type: 'activation' });
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string; details?: unknown };
      throw { code: e.code || 'functions/internal', message: e.message || 'Failed to send activation email.' };
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const fn = httpsCallable<{ email: string; type: 'reset' }, { success: boolean }>(
      functions,
      'sendAuthEmail'
    );
    try {
      await fn({ email: email.trim().toLowerCase(), type: 'reset' });
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      // Map Firebase Functions error codes to friendly auth-style codes.
      if (e.code === 'functions/not-found') {
        throw { code: 'auth/user-not-found', message: 'No account found with this email address.' };
      }
      throw { code: e.code || 'auth/internal-error', message: e.message || 'Failed to send reset email.' };
    }
  }, []);

  const updatePassword = useCallback(async (_newPassword: string) => {
    if (!user) throw new Error('Not authenticated');
    // Firebase Auth handles this via reauthentication flow
    console.log('Password update requested - use Firebase Auth reauthentication');
  }, [user]);

  /**
   * Refresh the current user profile from Firestore.
   * Call this after a write that the auth listener won't pick up
   * (e.g., creating the Firestore user doc right after sign-up).
   */
  const refreshUser = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', current.uid));
      if (userDoc.exists()) {
        setUser(buildUserFromFirestore(current.uid, userDoc.data()));
      }
    } catch (err) {
      console.error('Failed to refresh user profile:', err);
    }
  }, []);

  const updateProfile = useCallback(async (data: Partial<User>) => {
    if (!user) throw new Error('Not authenticated');

    const userDocRef = doc(db, 'users', user.uid);
    await updateDoc(userDocRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });

    // Update Firebase Auth display name/photo if provided
    if (auth.currentUser) {
      const updates: { displayName?: string; photoURL?: string } = {};
      if (data.displayName) updates.displayName = data.displayName;
      // Only pass actual URLs to Firebase Auth (not base64 - too long)
      if (data.photoURL && data.photoURL.startsWith('http')) {
        updates.photoURL = data.photoURL;
      }
      if (Object.keys(updates).length > 0) {
        await firebaseUpdateProfile(auth.currentUser, updates);
      }
    }

    // Update local state
    setUser(prev => prev ? { ...prev, ...data } : null);
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        needsPasswordMigration,
        login,
        register,
        logout,
        resetPassword,
        updatePassword,
        updateProfile,
        setMigratedPassword,
        refreshUser,
        sendActivationEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
